import type { Handler } from './handler';
import type { ApiFetchLoyaltyHubPort } from './ports/api-fetch-loyalty-hub.port';
import type { LoyaltyHubContent } from '~/types/loyalty-hub';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import { resolveAdapter } from '~/adapters/registry';
import loyaltyHubFallback from '~/assets/json/loyaltyHub.json';

// Enquanto o endpoint /loyalty-hub do core NÃO existe, não chamamos a API
// (evita o 404 no console/rede) e servimos sempre o fallback bundlado.
// Quando o core publicar o endpoint, é só trocar para `true` (ou remover o
// guard no início do handler abaixo).
const USE_API = false;

const CACHE_TTL_MS = 5 * 60 * 1000;

let cache: { data: LoyaltyHubContent; cachedAt: number } | null = null;
let inflight: Promise<LoyaltyHubContent | null> | null = null;

// Fallback bundlado: espelha o Figma e é a fonte enquanto o core não tem o
// endpoint /loyalty-hub. Tipado via cast pois o JSON é um shape mais largo.
const FALLBACK = loyaltyHubFallback as unknown as LoyaltyHubContent;

// Sempre resolve pra um conteúdo válido: API → cache → fallback bundlado.
// Nunca rejeita — o hub precisa abrir mesmo offline/sem backend.
export const fetchLoyaltyHubHandler: Handler<undefined, Ports, Result> = async (
  ports = {
    apiFetchLoyaltyHub: resolveAdapter('apiFetchLoyaltyHub'),
  },
) => {
  // Endpoint ainda não existe: pula a API e devolve o fallback direto,
  // sem tocar na rede (nada de 404). Remover quando `USE_API` virar true.
  if (!USE_API) {
    return FALLBACK;
  }

  if (cache && Date.now() - cache.cachedAt < CACHE_TTL_MS) {
    return cache.data;
  }

  if (inflight) {
    const [data] = await tryCatch(inflight);

    if (data) return data;
  }

  const promise = ports?.apiFetchLoyaltyHub.handle() ?? Promise.resolve(null);

  inflight = promise;

  const [content, error] = await tryCatch(promise);

  inflight = null;

  if (!error && content) {
    cache = { data: content, cachedAt: Date.now() };

    return content;
  }

  if (error) {
    console.error('[fetchLoyaltyHubHandler] falha ao buscar:', error);
  }

  // Sem API (endpoint ainda não existe) ou erro: cai no fallback do Figma.
  return cache?.data ?? FALLBACK;
};

export function clearLoyaltyHubCache(): void {
  cache = null;
  inflight = null;
}

type Ports = { apiFetchLoyaltyHub: ApiFetchLoyaltyHubPort };

type Result = Promise<LoyaltyHubContent>;
